import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, MapPin, Sparkles } from 'lucide-react';
import axios from 'axios';

const FullscreenView = ({ viewMode, facilities, facilitiesTotal = 0, loadingMore = false, onLoadMoreFacilities, products, productFilters = {}, selectedProduct, onViewChange, userLocations }) => {
  const [mcpHtml, setMcpHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch MCP UI HTML from middleware when viewMode or filters change
  useEffect(() => {
    if (viewMode === 'products' || viewMode === 'split') {
      fetchMCPResource(productFilters);
    }
  }, [viewMode, productFilters]);

  const fetchMCPResource = async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const middlewareUrl = process.env.REACT_APP_MCP_MIDDLEWARE_URL || 'https://fitpass.autopm.pro';

      // Build query string from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const url = `${middlewareUrl}/api/products-ui${queryString ? '?' + queryString : ''}`;

      console.log('Fetching MCP products with URL:', url);
      const response = await axios.get(url);
      console.log('MCP Resource response:', response.data);

      // Extract HTML from MCP UI resource structure
      // Structure: { type: "resource", resource: { uri, mimeType, text } }
      if (response.data?.resource?.text) {
        setMcpHtml(response.data.resource.text);
      } else if (response.data?.resource?.blob) {
        // Handle base64 encoded content
        setMcpHtml(atob(response.data.resource.blob));
      } else {
        console.error('Unexpected MCP resource structure:', response.data);
        setError('Invalid MCP resource format');
      }
    } catch (err) {
      console.error('Error fetching MCP resource:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Animation variants for smooth transitions
  const pageVariants = {
    initial: { opacity: 0, x: 100 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: [0.43, 0.13, 0.23, 0.96] // Custom easing
      }
    },
    exit: {
      opacity: 0,
      x: -100,
      transition: { duration: 0.3 }
    }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 50, scale: 0.9 },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.4 }
    }
  };

  const renderWelcome = () => (
    <motion.div
      key="welcome"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="welcome-view"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="welcome-content"
      >
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3
          }}
          className="welcome-icon"
        >
          <Sparkles size={80} />
        </motion.div>

        <h1>Witaj w FitPass Store!</h1>
        <p>Asystent AI pomoże Ci znaleźć idealny karnet sportowy</p>

        <motion.div
          className="welcome-actions"
          variants={staggerChildren}
          initial="initial"
          animate="animate"
        >
          <motion.button
            variants={cardVariants}
            whileHover={{ scale: 1.05, boxShadow: "0 10px 40px rgba(102, 126, 234, 0.4)" }}
            whileTap={{ scale: 0.95 }}
            className="cta-button primary"
            onClick={() => onViewChange('facilities')}
          >
            <MapPin size={24} />
            <div>
              <strong>Znajdź obiekty</strong>
              <span>Odkryj siłownie w Twojej okolicy</span>
            </div>
          </motion.button>

          <motion.button
            variants={cardVariants}
            whileHover={{ scale: 1.05, boxShadow: "0 10px 40px rgba(102, 126, 234, 0.4)" }}
            whileTap={{ scale: 0.95 }}
            className="cta-button secondary"
            onClick={() => onViewChange('products')}
          >
            <ShoppingBag size={24} />
            <div>
              <strong>Przeglądaj karnety</strong>
              <span>Zobacz dostępne pakiety FitPass</span>
            </div>
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  const renderFacilities = () => {
    const hasMoreFacilities = facilitiesTotal > facilities.length;

    return (
      <motion.div
        key="facilities"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="fullscreen-grid"
      >
        <motion.div
          className="grid-header"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <h2>Obiekty Sportowe</h2>
          <span className="count">
            {facilities.length} z {facilitiesTotal > 0 ? facilitiesTotal : facilities.length} obiektów
          </span>
        </motion.div>

        <motion.div
          className="cards-grid"
          variants={staggerChildren}
        >
          {facilities.map((facility, index) => (
            <motion.div
              key={facility.id}
              variants={cardVariants}
              custom={index}
              whileHover={{
                scale: 1.03,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)"
              }}
              className="facility-card-fullscreen"
            >
              <div className="card-image">
                <img src={facility.image || 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop'} alt={facility.name} />
                <div className="card-badge">{facility.facility_type}</div>
              </div>
              <div className="card-content">
                <h3>{facility.name}</h3>
                <p className="address">📍 {facility.address}</p>
                <div className="amenities">
                  {facility.amenities?.slice(0, 4).map((amenity, i) => (
                    <span key={i} className="amenity-tag">✓ {amenity}</span>
                  ))}
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="buy-pass-btn"
                  onClick={() => {
                    // Trigger AI agent to find products
                    onViewChange('split', {
                      facilities: [facility],
                      triggerProductSearch: true,
                      facilityContext: facility
                    });
                  }}
                >
                  🎫 Kup karnet
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Load More Button */}
        {hasMoreFacilities && (
          <motion.div
            className="load-more-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <motion.button
              className="load-more-btn"
              onClick={onLoadMoreFacilities}
              disabled={loadingMore}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loadingMore ? (
                <>Ładowanie...</>
              ) : (
                <>Załaduj więcej ({facilitiesTotal - facilities.length} pozostało)</>
              )}
            </motion.button>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // Note: Intent messages from iframe are handled directly by ImmersiveShoppingExperience
  // No forwarding needed since they share the same window context

  const renderProducts = () => (
    <motion.div
      key="products"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="fullscreen-grid"
    >
      <motion.div
        className="grid-header"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <h2>Dostępne Karnety</h2>
        <span className="count">{products.length} produktów</span>
      </motion.div>

      {/* Render MCP UI HTML via iframe */}
      <motion.div
        className="mcp-ui-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {loading ? (
          <div className="loading-state">Ładowanie produktów...</div>
        ) : error ? (
          <div className="error-state">Błąd: {error}</div>
        ) : mcpHtml ? (
          <iframe
            srcDoc={mcpHtml}
            className="mcp-iframe"
            sandbox="allow-scripts allow-same-origin"
            title="Products"
          />
        ) : (
          <div className="loading-state">Ładowanie produktów...</div>
        )}
      </motion.div>
    </motion.div>
  );

  const renderProductDetails = () => {
    if (!selectedProduct) return null;

    return (
      <motion.div
        key="productDetails"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="product-details-view"
      >
        <motion.div
          className="product-details-card"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="product-details-header">
            <motion.button
              className="back-btn"
              onClick={() => onViewChange('products')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              ← Wróć do produktów
            </motion.button>
            <span className="product-badge">{selectedProduct.card_type}</span>
          </div>

          <div className="product-details-content">
            <div className="product-image-section">
              <img
                src={selectedProduct.image_url || 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop'}
                alt={selectedProduct.title}
              />
            </div>

            <div className="product-info-section">
              <h1>{selectedProduct.title}</h1>

              <div className="price-section">
                <span className="price">${selectedProduct.price}</span>
                {selectedProduct.compare_at_price && (
                  <span className="compare-price">${selectedProduct.compare_at_price}</span>
                )}
                <span className="duration">/ {selectedProduct.duration_months} {selectedProduct.duration_months === 1 ? 'miesiąc' : 'miesięcy'}</span>
              </div>

              {selectedProduct.monthly_equivalent && (
                <p className="monthly-price">Równowartość: ${selectedProduct.monthly_equivalent}/miesiąc</p>
              )}

              <p className="description">{selectedProduct.description}</p>

              <div className="features-grid">
                <div className="feature-section">
                  <h3>Dostępne aktywności</h3>
                  <div className="facilities-list">
                    {selectedProduct.facilities?.map((facility, i) => (
                      <span key={i} className="facility-tag">✓ {facility}</span>
                    ))}
                  </div>
                </div>

                <div className="feature-section">
                  <h3>Dostępne miasta</h3>
                  <div className="cities-list">
                    {selectedProduct.cities?.map((city, i) => (
                      <span key={i} className="city-tag">📍 {city}</span>
                    ))}
                  </div>
                </div>

                <div className="feature-section">
                  <h3>Udogodnienia</h3>
                  <div className="amenities-list">
                    <span className={`amenity ${selectedProduct.includes_personal_training ? 'included' : 'not-included'}`}>
                      {selectedProduct.includes_personal_training ? '✓' : '✗'} Trening personalny
                    </span>
                    <span className={`amenity ${selectedProduct.includes_swimming_pool ? 'included' : 'not-included'}`}>
                      {selectedProduct.includes_swimming_pool ? '✓' : '✗'} Basen
                    </span>
                    <span className={`amenity ${selectedProduct.includes_spa ? 'included' : 'not-included'}`}>
                      {selectedProduct.includes_spa ? '✓' : '✗'} SPA
                    </span>
                  </div>
                </div>

                <div className="feature-section">
                  <h3>Limity</h3>
                  <p className="bookings-info">
                    📅 {selectedProduct.max_bookings_per_month === 999
                      ? 'Nielimitowane rezerwacje'
                      : `${selectedProduct.max_bookings_per_month} rezerwacji/miesiąc`}
                  </p>
                </div>
              </div>

              <motion.button
                className="buy-now-btn"
                whileHover={{ scale: 1.02, boxShadow: "0 10px 40px rgba(102, 126, 234, 0.4)" }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  // Trigger purchase intent
                  window.postMessage({
                    type: 'intent',
                    payload: { intent: 'purchase', params: { productId: selectedProduct.id } }
                  }, '*');
                }}
              >
                🛒 Kup teraz za ${selectedProduct.price}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const renderSplitView = () => (
    <motion.div
      key="split"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="split-view"
    >
      <motion.div
        className="split-panel facilities-panel"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {renderFacilities()}
      </motion.div>

      <motion.div
        className="split-panel products-panel"
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        {renderProducts()}
      </motion.div>
    </motion.div>
  );

  return (
    <div className="fullscreen-view-container">
      <AnimatePresence mode="wait">
        {viewMode === 'welcome' && renderWelcome()}
        {viewMode === 'facilities' && renderFacilities()}
        {viewMode === 'products' && renderProducts()}
        {viewMode === 'productDetails' && renderProductDetails()}
        {viewMode === 'split' && renderSplitView()}
      </AnimatePresence>

      <style jsx>{`
        .fullscreen-view-container {
          width: 100%;
          height: 100%;
          overflow-y: auto;
          padding: 2rem;
        }

        .welcome-view {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .welcome-content {
          text-align: center;
          color: white;
          max-width: 600px;
        }

        .welcome-icon {
          margin-bottom: 2rem;
          color: #fbbf24;
        }

        .welcome-content h1 {
          font-size: 3rem;
          margin-bottom: 1rem;
          font-weight: 800;
        }

        .welcome-content p {
          font-size: 1.25rem;
          margin-bottom: 3rem;
          opacity: 0.9;
        }

        .welcome-actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .cta-button {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          padding: 1.5rem 2rem;
          background: white;
          border: none;
          border-radius: 16px;
          cursor: pointer;
          text-align: left;
          transition: all 0.3s;
        }

        .cta-button div {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .cta-button strong {
          font-size: 1.125rem;
          color: #1f2937;
        }

        .cta-button span {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .fullscreen-grid {
          display: flex;
          flex-direction: column;
          min-height: 100%;
        }

        .grid-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          color: white;
        }

        .grid-header h2 {
          font-size: 2rem;
          font-weight: 700;
        }

        .count {
          background: rgba(255, 255, 255, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 2rem;
          overflow-y: auto;
          padding-bottom: 2rem;
        }

        .facility-card-fullscreen {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          height: 100%;
          max-height: 550px;
        }

        .card-image {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .card-badge {
          position: absolute;
          top: 1rem;
          left: 1rem;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .card-content {
          padding: 1.5rem;
        }

        .card-content h3 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #1f2937;
        }

        .address {
          color: #6b7280;
          margin-bottom: 1rem;
        }

        .amenities {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .amenity-tag {
          background: #f3f4f6;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          color: #374151;
        }

        .buy-pass-btn {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          font-size: 1rem;
        }

        .load-more-container {
          display: flex;
          justify-content: center;
          padding: 2rem 0;
          margin-top: 1rem;
        }

        .load-more-btn {
          padding: 1rem 3rem;
          background: rgba(255, 255, 255, 0.95);
          color: #667eea;
          border: 2px solid rgba(102, 126, 234, 0.3);
          border-radius: 16px;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .load-more-btn:hover:not(:disabled) {
          background: white;
          border-color: #667eea;
          box-shadow: 0 8px 30px rgba(102, 126, 234, 0.3);
        }

        .load-more-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .split-view {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          height: 100%;
        }

        .split-panel {
          overflow-y: auto;
        }

        .mcp-ui-container {
          flex: 1;
          width: 100%;
          display: flex;
          align-items: stretch;
          justify-content: center;
        }

        .mcp-iframe {
          width: 100%;
          min-height: 2000px;
          border: none;
          border-radius: 12px;
          background: transparent;
        }

        .loading-state,
        .error-state {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
          color: white;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .error-state {
          color: #ff6b6b;
        }

        /* Product Details Styles */
        .product-details-view {
          height: 100%;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 1rem;
        }

        .product-details-card {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 1200px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }

        .product-details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .back-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .back-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .product-badge {
          background: white;
          color: #667eea;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .product-details-content {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 2rem;
          padding: 2rem;
        }

        .product-image-section img {
          width: 100%;
          height: 300px;
          object-fit: cover;
          border-radius: 16px;
        }

        .product-info-section h1 {
          font-size: 2rem;
          font-weight: 800;
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .price-section {
          display: flex;
          align-items: baseline;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .price {
          font-size: 2.5rem;
          font-weight: 800;
          color: #667eea;
        }

        .compare-price {
          font-size: 1.25rem;
          color: #9ca3af;
          text-decoration: line-through;
        }

        .duration {
          font-size: 1rem;
          color: #6b7280;
        }

        .monthly-price {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 1.5rem;
        }

        .description {
          font-size: 1rem;
          color: #4b5563;
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .feature-section h3 {
          font-size: 0.875rem;
          font-weight: 700;
          color: #374151;
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .facilities-list,
        .cities-list,
        .amenities-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .facility-tag,
        .city-tag {
          background: #f3f4f6;
          padding: 0.375rem 0.75rem;
          border-radius: 8px;
          font-size: 0.875rem;
          color: #374151;
        }

        .amenity {
          padding: 0.375rem 0.75rem;
          border-radius: 8px;
          font-size: 0.875rem;
        }

        .amenity.included {
          background: #dcfce7;
          color: #166534;
        }

        .amenity.not-included {
          background: #fee2e2;
          color: #991b1b;
        }

        .bookings-info {
          font-size: 1rem;
          color: #4b5563;
        }

        .buy-now-btn {
          width: 100%;
          padding: 1.25rem 2rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 1.25rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        @media (max-width: 768px) {
          .product-details-content {
            grid-template-columns: 1fr;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default FullscreenView;
